import {
  BaseEventService,
  type ServiceActivationContext,
} from '@realitycollective/service-framework';
import { createLogger } from '../../util/logger';
import { RoomTopics } from '../../protocol/roomTopics';
import {
  TRAINING_STATE_PRESENT,
  parseTrainingEvent,
  trainingActionJson,
  trainingControlJson,
  trainingScenarioJson,
  type TrainingChannelEvent,
  type TrainingFlowStatus,
  type TrainingScenario,
  type TrainingStep,
  type TrainingStepResult,
} from '../../protocol/trainingChannelMessages';
import { toRenderBatch } from '../../rendering/DetectionMath';
import type { RenderableDetection } from '../../rendering/types';
import type { IDetectionService } from '../detection/IDetectionService';
import type { ILiveKitRoomService } from '../room/ILiveKitRoomService';
import type {
  ITrainingStateService,
  TrainingDetectionMatch,
  TrainingStateEventMap,
} from './ITrainingStateService';

const log = createLogger('Training');

export interface RemoteTrainingConfig {
  /** Flip Y when mapping stream boxes to the viewport (bbox y-down -> viewport y-up). Default true. */
  readonly invertY?: boolean;
  /** Scenarios the hand menu's SWAP picker offers. Empty = no picker. */
  readonly catalog?: readonly TrainingScenario[];
  /** Test seam: arrival clock (ms). Default `performance.now()`. */
  readonly nowMs?: () => number;
}

interface LastArrival {
  readonly detections: readonly RenderableDetection[];
  readonly arrivalTimeMs: number;
}

/**
 * The SERVER-authority training flow: the machine runs on the
 * QuestVisionStreamServer (QVS_TRAINING_SM) and this service MIRRORS it - no
 * local state machine at all. Port of the Unity `RemoteTrainingStateService`.
 *
 * - Inbound: `{"type":"training"}` events on the room's `training` topic.
 *   Every event carries a full state snapshot, so mirroring is idempotent - a
 *   missed event is healed by the next one, and a `sync` request goes out
 *   whenever an event references a scenario this client hasn't seen.
 * - Outbound: form actions, flow control and scenario pushes as wire messages
 *   on the same topic (`sendDetectionMessage` = the training topic on the
 *   room transport).
 * - World labels: geometry stays client-side - step activations are matched
 *   against the most recently received detections batch, and re-sightings of
 *   the active step's class are computed locally from the live stream.
 */
export class RemoteTrainingStateService
  extends BaseEventService<TrainingStateEventMap, RemoteTrainingConfig>
  implements ITrainingStateService
{
  private readonly room: ILiveKitRoomService;
  private readonly detections: IDetectionService;
  private readonly unsubs: Array<() => void> = [];

  private _scenario: TrainingScenario | null = null;
  private _status: TrainingFlowStatus = 'idle';
  private _stepIndex = -1;
  private _expectedClass = '';
  private _expectedState = '';
  private _discarded = 0;
  private readonly registry = new Map<string, string>();
  private lastArrival: LastArrival | null = null;
  private syncRequested = false;
  private _activeScenarioIndex = -1;

  constructor(
    context: ServiceActivationContext<RemoteTrainingConfig>,
    room: ILiveKitRoomService,
    detections: IDetectionService,
  ) {
    super(context);
    this.room = room;
    this.detections = detections;
  }

  get scenario(): TrainingScenario | null {
    return this._scenario;
  }

  get status(): TrainingFlowStatus {
    return this._status;
  }

  get currentStepIndex(): number {
    return this._stepIndex;
  }

  get currentStep(): TrainingStep | null {
    const scenario = this._scenario;
    if (!scenario || this._stepIndex < 0 || this._stepIndex >= scenario.steps.length) return null;
    return scenario.steps[this._stepIndex]!;
  }

  get expectedClass(): string {
    return this._expectedClass;
  }

  get expectedState(): string {
    return this._expectedState;
  }

  get detectedObjects(): ReadonlyMap<string, string> {
    return this.registry;
  }

  get discardedCount(): number {
    return this._discarded;
  }

  get scenarioCatalog(): readonly string[] {
    return (this.serviceConfig.catalog ?? []).map((s) => s.name);
  }

  get activeScenarioIndex(): number {
    return this._activeScenarioIndex;
  }

  private nowMs(): number {
    return this.serviceConfig.nowMs ? this.serviceConfig.nowMs() : performance.now();
  }

  override start(): void {
    this.unsubs.push(
      this.room.on('data', (message) => {
        if (message.topic === RoomTopics.training) this.onChannelMessage(message.json);
      }),
      // Channel (re)opened - ask the server where the flow stands, so a
      // reconnect or late join rebuilds the mirror (and the UX) in one hop.
      this.room.on('ready', () => {
        this.syncRequested = this.send(trainingControlJson('sync'));
      }),
      this.detections.on('detections', (payload) => {
        const batch = toRenderBatch(payload, { invertY: this.serviceConfig.invertY ?? true });
        this.onDetections({ detections: batch.detections, arrivalTimeMs: this.nowMs() });
      }),
    );
    log.info('server authority - mirroring the QuestVisionStreamServer training flow (QVS_TRAINING_SM)');
  }

  override destroy(): void {
    this.unsubs.forEach((unsub) => unsub());
    this.unsubs.length = 0;
    super.destroy();
  }

  // ------------------------------------------------------------ flow control

  begin(): void {
    if (!this.send(trainingControlJson('begin'))) {
      log.warn('server authority: begin() with no active session - is the server up (and QVS_TRAINING_SM enabled)?');
    }
  }

  resetScenario(): void {
    this.send(trainingControlJson('reset'));
    // Reflect idle immediately so the UX (restart/exit) does not wait a round
    // trip; the server's next event re-syncs the mirror anyway.
    this._status = 'idle';
    this._stepIndex = -1;
    this._expectedClass = '';
    this._expectedState = '';
    this.registry.clear();
    log.info('reset requested (mirror set idle)');
  }

  completeStep(result: TrainingStepResult): void {
    if (this._status !== 'running') return;
    if (result.stepIndex !== this._stepIndex) {
      log.warn(`stale step result for step ${result.stepIndex} ignored (current ${this._stepIndex})`);
      return;
    }
    if (!this.send(trainingActionJson(result))) {
      log.warn(`server authority: action '${result.optionLabel}' NOT sent - no active session`);
      return;
    }
    log.info(`action '${result.optionLabel}' -> sent to server (${result.resultClass}/${result.resultState}, step ${result.stepIndex + 1})`);
  }

  loadScenario(scenario: TrainingScenario): void {
    // Push to the server - it decides whether to accept; its 'loaded' event
    // (echoing the scenario) is what updates this mirror.
    if (this.send(trainingScenarioJson(scenario))) log.info(`scenario '${scenario.name}' pushed to server`);
    else log.warn('server authority: scenario push failed - no active session');
  }

  switchScenario(index: number): boolean {
    const catalog = this.serviceConfig.catalog ?? [];
    const scenario = catalog[index];
    if (!scenario) {
      log.warn(`switchScenario(${index}) rejected - catalog holds ${catalog.length} scenario(s)`);
      return false;
    }
    if (!this.send(trainingScenarioJson(scenario))) {
      log.warn(`server authority: switch to '${scenario.name}' NOT sent - no active session`);
      return false;
    }
    this._activeScenarioIndex = index;
    log.info(`switch to '${scenario.name}' pushed to server`);
    return true;
  }

  // ----------------------------------------------------------------- inbound

  private onChannelMessage(json: string): void {
    const event = parseTrainingEvent(json);
    if (!event) return;

    // Snapshot first - every event carries it, so the mirror is correct
    // before any UX event fires.
    this.applySnapshot(event);

    switch (event.kind) {
      case 'loaded':
        this._scenario = event.scenario;
        log.info(`scenario '${event.scenario!.name}' loaded from server (${event.scenario!.steps.length} steps)`);
        this.emit('scenarioLoaded', event.scenario!);
        break;

      case 'step': {
        if (!this.ensureScenario(event)) return;
        const step = this.currentStep;
        if (!step) {
          log.warn(`server step ${event.stepIndex} outside the mirrored scenario - sync requested`);
          this.requestSync();
          return;
        }
        const match = this.findMatch(event);
        log.info(
          `'${event.arrivedClass}/${event.arrivedState}' (${event.source}) -> step ${this._stepIndex + 1}/${this._scenario!.steps.length} '${step.title}', expecting '${this._expectedClass}/${this._expectedState}'`,
        );
        this.emit('stepActivated', { step, stepIndex: this._stepIndex, stepCount: this._scenario!.steps.length, match });
        break;
      }

      case 'completed':
        log.info(`scenario complete on '${event.arrivedClass}/${event.arrivedState}' (${event.source})`);
        this.emit('scenarioCompleted', undefined);
        break;

      case 'state': {
        this.syncRequested = false;
        if (!this.ensureScenario(event)) return;
        // A snapshot mid-run (reconnect/late join): re-present the active
        // step so the UX catches up.
        const step = this.currentStep;
        if (this._status === 'running' && step) {
          log.info(`state sync -> step ${this._stepIndex + 1}/${this._scenario!.steps.length} '${step.title}'`);
          this.emit('stepActivated', { step, stepIndex: this._stepIndex, stepCount: this._scenario!.steps.length, match: null });
        }
        break;
      }
    }
  }

  /**
   * The single detection tap: caches the latest batch for step-activation
   * geometry, and computes re-sightings of the active step's detected class
   * locally - the label and geometry are right here; no round trip needed to
   * keep the world label tracking.
   */
  private onDetections(arrival: LastArrival): void {
    this.lastArrival = arrival;
    const step = this.currentStep;
    if (this._status !== 'running' || !step || step.detectedClass.length === 0) return;

    for (const detection of arrival.detections) {
      if (detection.label.toLowerCase() !== step.detectedClass.toLowerCase()) continue;
      this.emit('currentClassSighted', this.toMatch(detection, this.registryState(detection.label), arrival.arrivalTimeMs, 'detection'));
      return;
    }
  }

  private applySnapshot(event: TrainingChannelEvent): void {
    this._status = event.status;
    this._stepIndex = event.stepIndex;
    this._expectedClass = event.expectedClass;
    this._expectedState = event.expectedState;
    this._discarded = event.discardedCount;
    this.registry.clear();
    for (const [key, value] of event.detectedObjects) this.registry.set(key, value);
  }

  /** Adopt an event-embedded scenario, or request a sync when the mirror has none. */
  private ensureScenario(event: TrainingChannelEvent): boolean {
    if (event.scenario) this._scenario = event.scenario;
    if (!this._scenario) {
      log.warn('server event before any scenario - sync requested');
      this.requestSync();
      return false;
    }
    return true;
  }

  /**
   * Geometry for a detection-driven activation: the training event arrives
   * right after the detections payload that caused it, so the cached latest
   * batch is (almost always) that payload. Action-driven activations have no
   * world position (null match).
   */
  private findMatch(event: TrainingChannelEvent): TrainingDetectionMatch | null {
    if (event.source === 'action' || event.arrivedClass.length === 0 || !this.lastArrival) return null;
    for (const detection of this.lastArrival.detections) {
      if (detection.label.toLowerCase() === event.arrivedClass.toLowerCase()) {
        return this.toMatch(detection, event.arrivedState, this.lastArrival.arrivalTimeMs, event.source);
      }
    }
    return null;
  }

  private toMatch(
    detection: RenderableDetection,
    state: string,
    arrivalTimeMs: number,
    source: TrainingDetectionMatch['source'],
  ): TrainingDetectionMatch {
    return {
      label: detection.label,
      state,
      conf: detection.conf,
      center: detection.center,
      rect: detection.rect,
      arrivalTimeMs,
      source,
    };
  }

  private registryState(className: string): string {
    return this.registry.get(className.toLowerCase()) ?? TRAINING_STATE_PRESENT;
  }

  private requestSync(): void {
    if (!this.syncRequested) this.syncRequested = this.send(trainingControlJson('sync'));
  }

  private send(json: string): boolean {
    return this.room.sendDetectionMessage(json);
  }
}
